/**
 * staffAuthStore — Staff auth & profile slice (Zustand).
 *
 *   GET   /api/staff/me
 *   PATCH /api/staff/me
 *   POST  /api/staff/auth/login
 *   POST  /api/staff/auth/logout
 *   POST  /api/staff/auth/refresh
 *   POST  /api/staff/auth/change-password
 *   POST  /api/staff/auth/forgot-password
 *   POST  /api/staff/auth/reset-password
 */
import { apiRequest } from "./staffUtils";
import { toDisplayString } from "../../utils/toDisplayString";
import authSession from "../../lib/authSession";

export const staffAuthInitialState = {
  staffToken: null,
  staffUser: null,
  staffIsAuthenticated: false,

  staffProfile: null,
  staffProfileLoading: false,
  staffProfileError: null,

  staffLoginLoading: false,
  staffLoginError: null,

  staffAuthActionLoading: false,
  staffAuthActionError: null,
};

export function createStaffAuthActions(set, get) {
  /** Run an auth POST/PATCH that returns no significant payload. */
  async function runAction(path, method, body, successMessage) {
    set({ staffAuthActionLoading: true, staffAuthActionError: null });
    try {
      // Auth endpoint → cookies only.
      const data = await apiRequest(get, path, { method, body, credentials: "include" });
      set({
        staffAuthActionLoading: false,
        staffAuthActionError: null,
        successMessage,
      });
      return { success: true, data };
    } catch (err) {
      const msg = toDisplayString(err.message, "Request failed");
      set({ staffAuthActionLoading: false, staffAuthActionError: msg });
      return { success: false, error: msg };
    }
  }

  return {
    // POST /api/staff/auth/login
    staffLogin: async (email, password) => {
      set({ staffLoginLoading: true, staffLoginError: null });
      try {
        // Auth endpoint → cookies only; the body token is kept in memory.
        const data = await apiRequest(get, "/api/staff/auth/login", {
          method: "POST",
          body: { email, password },
          token: "",
          credentials: "include",
        });
        const user = data.user || data.staff || data.data?.user || null;
        // Auth endpoint = cookie-based. The access token from the body is kept
        // IN MEMORY for every other endpoint's Bearer header.
        const token = authSession.findAccessToken(data) || null;
        authSession.setSession({
          token,
          refreshToken: data?.refreshToken || data?.data?.refreshToken || null,
          role: "staff",
          user,
        });
        set({
          staffLoginLoading: false,
          staffLoginError: null,
          staffToken: token || get().staffToken,
          staffUser: user,
          staffIsAuthenticated: true,
        });
        return { success: true, user, data };
      } catch (err) {
        const msg = toDisplayString(err.message, "Login failed");
        set({ staffLoginLoading: false, staffLoginError: msg });
        return { success: false, error: msg };
      }
    },

    // POST /api/staff/auth/logout
    staffLogout: async () => {
      set({ staffAuthActionLoading: true, staffAuthActionError: null });
      try {
        // Auth endpoint → cookies only.
        await apiRequest(get, "/api/staff/auth/logout", {
          method: "POST",
          credentials: "include",
        });
      } catch {
        // Best-effort: clear local state regardless.
      }
      // The request above already hit POST /api/staff/auth/logout with the
      // bearer token; only the local session needs clearing now.
      authSession.clearSession();
      set({
        staffAuthActionLoading: false,
        staffToken: null,
        staffUser: null,
        staffIsAuthenticated: false,
        staffProfile: null,
      });
      return { success: true };
    },

    // POST /api/staff/auth/refresh — the cookies re-authenticate the session.
    staffRefresh: async () => {
      set({ staffAuthActionLoading: true, staffAuthActionError: null });
      try {
        const session = await authSession.refresh("staff");
        set({
          staffAuthActionLoading: false,
          staffIsAuthenticated: true,
          staffUser: session?.user ?? get().staffUser,
          staffToken: session?.token ?? get().staffToken,
        });
        return { success: true };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to refresh session");
        set({ staffAuthActionLoading: false, staffAuthActionError: msg });
        return { success: false, error: msg };
      }
    },

    // POST /api/staff/auth/change-password
    staffChangePassword: (payload) =>
      runAction("/api/staff/auth/change-password", "POST", payload, "Password changed successfully"),

    // POST /api/staff/auth/forgot-password
    staffForgotPassword: (payload) =>
      runAction("/api/staff/auth/forgot-password", "POST", payload, "Reset link sent"),

    // POST /api/staff/auth/reset-password
    staffResetPassword: (payload) =>
      runAction("/api/staff/auth/reset-password", "POST", payload, "Password reset successfully"),

    // GET /api/staff/me
    fetchStaffProfile: async () => {
      set({ staffProfileLoading: true, staffProfileError: null });
      try {
        const data = await apiRequest(get, "/api/staff/me");
        const profile = data.user || data.staff || data.data || data;
        set({ staffProfileLoading: false, staffProfile: profile });
        return { success: true, profile };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to fetch profile");
        set({ staffProfileLoading: false, staffProfileError: msg });
        return { success: false, error: msg };
      }
    },

    // PATCH /api/staff/me
    updateStaffProfile: async (payload) => {
      set({ staffProfileLoading: true, staffProfileError: null });
      try {
        const data = await apiRequest(get, "/api/staff/me", { method: "PATCH", body: payload });
        const profile = data.user || data.staff || data.data || data;
        set({
          staffProfileLoading: false,
          staffProfile: profile,
          successMessage: "Profile updated successfully",
        });
        return { success: true, profile };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to update profile");
        set({ staffProfileLoading: false, staffProfileError: msg });
        return { success: false, error: msg };
      }
    },
  };
}

const staffAuthActions = createStaffAuthActions;
export default staffAuthActions;
